import {
  getBusinessPaymentMethods,
  createRazorpayOrder,
  verifyRazorpayPayment,
  createCashfreeOrder,
  verifyCashfreePayment,
} from "./payment.service";

const loadScript = (src, isReady) =>
  new Promise((resolve, reject) => {
    if (isReady()) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not load payment gateway"));
    document.body.appendChild(s);
  });

const payWithRazorpay = async (businessUuid, amount, customer, businessName) => {
  await loadScript(
    "https://checkout.razorpay.com/v1/checkout.js",
    () => !!window.Razorpay,
  );
  const order = await createRazorpayOrder(businessUuid, amount, {
    customerName: customer.name,
    customerPhone: customer.phone,
    orderId: `sale_${Date.now()}`,
  });
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key,
      amount: order.amount,
      currency: order.currency,
      name: businessName || "Store",
      description: "Sale Payment",
      order_id: order.orderId,
      prefill: { name: customer.name, contact: customer.phone },
      handler: async (r) => {
        try {
          const v = await verifyRazorpayPayment(businessUuid, {
            razorpay_payment_id: r.razorpay_payment_id,
            razorpay_order_id: r.razorpay_order_id,
            razorpay_signature: r.razorpay_signature,
          });
          if (v.success) resolve({ paymentId: v.paymentId || r.razorpay_payment_id });
          else reject(new Error("Payment verification failed"));
        } catch (e) {
          reject(e);
        }
      },
      modal: { ondismiss: () => reject(new Error("CANCELLED")) },
    });
    rzp.on?.("payment.failed", (e) =>
      reject(new Error(e?.error?.description || "Payment failed")),
    );
    rzp.open();
  });
};

const payWithCashfree = async (businessUuid, amount, customer) => {
  await loadScript(
    "https://sdk.cashfree.com/js/v3/cashfree.js",
    () => !!window.Cashfree,
  );
  const response = await createCashfreeOrder(businessUuid, amount, {
    customerDetails: {
      customer_id: `guest_${Date.now()}`,
      customer_name: customer.name || "Customer",
      customer_email: "customer@example.com",
      customer_phone: customer.phone || "9999999999",
    },
    orderId: `sale_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  });
  if (!response.success) throw new Error("Could not start payment");
  const cashfree = new window.Cashfree({ mode: response.mode || "production" });
  const result = await cashfree.checkout({
    paymentSessionId: response.paymentSessionId,
    redirectTarget: "_modal",
  });
  if (result?.error) throw new Error(result.error.message || "Payment failed");
  const v = await verifyCashfreePayment(businessUuid, { orderId: response.orderId });
  if (!v.success) throw new Error("CANCELLED");
  return { paymentId: v.paymentId };
};

// Opens the gateway's own checkout (QR, UPI, cards...) and resolves once the payment is verified.
export const collectSalePayment = async ({
  businessUuid,
  amount,
  customer = {},
  businessName,
}) => {
  const res = await getBusinessPaymentMethods(businessUuid);
  const d = res?.data || {};
  let gateway = d.enabledGateway;
  if (gateway !== "razorpay" && gateway !== "cashfree") {
    gateway = d.cashfree?.isVerified
      ? "cashfree"
      : d.razorpay?.isVerified
        ? "razorpay"
        : null;
  }
  if (!gateway) throw new Error("No payment gateway is enabled for this business");
  return gateway === "razorpay"
    ? payWithRazorpay(businessUuid, amount, customer, businessName)
    : payWithCashfree(businessUuid, amount, customer);
};
